import { Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useId, useState } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";
import { FilterSectionHeader } from "./FilterSectionHeader";

/** How a group is framed. `outlined` draws a hairline box around its fields; `plain` draws nothing. */
export type FilterFieldGroupFrame = "plain" | "outlined";

/**
 * How a group titles itself.
 *
 * `section` is the rail's own section title, shared with `FacetFilterSection` — use it for a top-level
 * group so a rail of mixed facet and control groups reads as one panel. `micro` is the small uppercase
 * label, for a subordinate group NESTED inside a section, where a second section-weight title would
 * compete with the real one.
 */
export type FilterFieldGroupTitleVariant = "section" | "micro";

/**
 * Per-frame spacing, so an `outlined` group reads as one object and a `plain` one reads as a run of
 * independent fields.
 *
 * `outlined` is tighter on purpose: inside a drawn box, generous gaps make the border look like it is
 * holding nothing together. `plain`'s wider gap is the rail's own field rhythm.
 */
const FRAME_SPACING: Record<FilterFieldGroupFrame, { titleGap: number; fieldGap: number }> = {
	plain: { titleGap: 0.3, fieldGap: 2 },
	outlined: { titleGap: 1.5, fieldGap: 1 },
};

type FilterFieldGroupProps = {
	/** Already-translated group title. */
	title?: string;
	/**
	 * `plain` (default) is a bare vertical run of fields — the rail's normal rhythm. `outlined` draws a
	 * hairline box, for a set of fields that only mean something together (a shift date + its times:
	 * the times are meaningless without the date, and the box is what says so).
	 *
	 * The border is deliberately the PANEL's own hairline, not a heavier one — a group drawn more
	 * strongly than its container inverts the hierarchy and the group starts reading as the subject.
	 */
	frame?: FilterFieldGroupFrame;
	/** Which title treatment to use. Defaults to the rail's shared section title. */
	titleVariant?: FilterFieldGroupTitleVariant;
	/**
	 * Give the group a collapse chevron. Only meaningful with a `section` title — a `micro`-titled
	 * subordinate group is already small enough that hiding it saves nothing.
	 */
	collapsible?: boolean;
	/** Whether a `collapsible` group starts open. Default true. */
	defaultExpanded?: boolean;
	/** Active filters inside this group, shown beside the title — see `FilterSectionHeader`'s `activeCount`. */
	activeCount?: number;
	/** Override the gap between fields. Defaults per `frame` — pass only to fix a genuine mismatch. */
	fieldGap?: number;
	children: ReactNode;
	dataTestId?: string;
};

/**
 * A titled, optionally-framed vertical stack of **heterogeneous** filter controls.
 *
 * The counterpart to `FacetFilterSection`: that one owns a checkbox facet group with counts, this one
 * owns "a date, a time range and a switch that belong together". Neither replaces the other — a rail
 * usually holds both, which is why they share `FilterSectionHeader` rather than each drawing a title.
 *
 * Compose directly, or let `FilterFieldList` render it from config.
 */
export const FilterFieldGroup: FC<FilterFieldGroupProps> = ({
	title,
	// DEFAULT: unframed — the rail's normal field rhythm, which is what most groups want.
	frame = "plain",
	// DEFAULT: the shared section title, so a top-level group matches the facet groups beside it.
	titleVariant = "section",
	collapsible = false,
	// DEFAULT: open — a filter the user cannot see is a filter they forget is applied.
	defaultExpanded = true,
	activeCount,
	fieldGap,
	children,
	dataTestId,
}) => {
	const titleId = useId();
	const [expanded, setExpanded] = useState(defaultExpanded);
	const spacing = FRAME_SPACING[frame];
	const gap = fieldGap ?? spacing.fieldGap;
	// Collapsing needs something to collapse INTO, so it only applies to a real section title.
	const canCollapse = collapsible && !!title && titleVariant === "section";

	const fields = (
		<Box sx={{ display: "flex", flexDirection: "column", gap }} data-testid={dataTestId ? `${dataTestId}-fields` : undefined}>
			{children}
		</Box>
	);

	const framedSx = frame === "outlined" ? { border: `1px solid ${NEUTRAL[200]}`, p: 0.75, borderRadius: 1 } : {};

	// Untitled: the group's only job is the stack (plus a frame, if asked for).
	if (!title) {
		return frame === "outlined" ? (
			<Box sx={framedSx} data-testid={dataTestId}>
				{fields}
			</Box>
		) : (
			<Box sx={{ display: "flex", flexDirection: "column", gap }} data-testid={dataTestId}>
				{children}
			</Box>
		);
	}

	return (
		<Box
			role="group"
			aria-labelledby={titleId}
			data-testid={dataTestId}
			sx={{
				display: "flex",
				flexDirection: "column",
				// A section header carries its own vertical padding, so it needs no extra gap under it.
				gap: titleVariant === "section" ? 0 : spacing.titleGap,
				...framedSx,
			}}
		>
			{titleVariant === "section" ? (
				<FilterSectionHeader
					title={title}
					titleId={titleId}
					expanded={canCollapse ? expanded : undefined}
					onToggle={canCollapse ? () => setExpanded((open) => !open) : undefined}
					activeCount={activeCount}
					dataTestId={dataTestId ? `${dataTestId}-header` : undefined}
				/>
			) : (
				<Typography
					id={titleId}
					sx={{
						pl: 0.25,
						fontSize: FONT_SIZE.LABEL_XS,
						color: INK.MUTED,
						fontWeight: 600,
						textTransform: "uppercase",
						// See FilterFieldRow: pinned to the font size so the title adds no leading of its own.
						lineHeight: FONT_SIZE.LABEL_XS,
					}}
				>
					{title}
				</Typography>
			)}

			{(!canCollapse || expanded) && (
				<Box
					sx={{
						// Matches FacetFilterSection's body: opacity-only entrance (GPU-cheap), height snaps so
						// collapse feels instant rather than sluggish.
						...(titleVariant === "section" ? { pb: 0.5 } : {}),
						"@keyframes filterGroupFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: canCollapse ? `filterGroupFadeIn ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}` : "none",
						"@media (prefers-reduced-motion: reduce)": { animation: "none" },
					}}
				>
					{fields}
				</Box>
			)}
		</Box>
	);
};
